import { FC, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@hooks/store';
import { getSuperSimplifierAccountsAsync, getSuperSimplifierAccountAsync } from '@store/reducers/supersimplifier/thunks';
import { ScreenState } from '@src/app/enums';
import { SupersimplifierStore, selectSupersimplifier, setSuperSimplifierAccountParameter } from '@store/reducers/supersimplifier';
import Container from '@mui/material/Container';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { PageContent } from '@components/index';
import SupersimplifierTable, { SupersimplifierTableRow, TableHeaders } from '@components/superTables/supersimplifierTable';
import { homeUrl } from '@utils/UrlBuilder';
import { Alert, FormControl, Box, InputLabel, Typography, Breadcrumbs } from '@mui/material';
import { SuperSimplifierAccountSummary } from 'clientportalshared';
import { Home } from '@mui/icons-material';
import { useHistory } from 'react-router';

const Insurance: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const supersimplifierStore: SupersimplifierStore = useAppSelector(selectSupersimplifier);

  const [selectedAccount, setSelectedAccount] = useState('');

  const getSuperSimplifierAccount = async (accountLis: SuperSimplifierAccountSummary[] | undefined) => {
    if (accountLis?.length && accountLis[0].accountNumber) {
      await dispatch(
        getSuperSimplifierAccountAsync({
          accountNumber: accountLis[0].accountNumber,
        })
      );
    }
  };

  const getPageData = () => {
    dispatch(getSuperSimplifierAccountsAsync())
      .unwrap()
      .then((responce) => {
        if (responce?.data?.length && responce.data[0].accountNumber) {
          setSelectedAccount(responce.data[0].accountNumber);
        }
        getSuperSimplifierAccount(responce.data);
      });
  };

  useEffect(() => {
    getPageData();
  }, []);

  const tableHeaders: TableHeaders = { header1: 'Description', header2: 'Value' };
  let coverSummaryTable: SupersimplifierTableRow[] = [];
  let premiumDetailsTable: SupersimplifierTableRow[] = [];

  if (supersimplifierStore.superSimplifierAccount?.insurances?.length) {
    const { deathInsuredAmount, incomeProtectionInsuredAmount, premiumAmount, premiumFrequency, totalPermanentDisabilityInsuredAmount } =
      supersimplifierStore.superSimplifierAccount?.insurances[0];

    coverSummaryTable = [
      { label: 'Death', value: deathInsuredAmount },
      {
        label: 'Total Permanent Disability (TPD)',
        value: totalPermanentDisabilityInsuredAmount,
      },
      {
        label: 'Income Protection (IP)',
        value: incomeProtectionInsuredAmount,
      },
    ];

    premiumDetailsTable = [
      { label: 'Premium Amount', value: premiumAmount },
      {
        label: 'Frequency',
        value: premiumFrequency,
      },
    ];
  }

  const handleAccountChange = (event: SelectChangeEvent) => {
    setSelectedAccount(event.target.value);
    dispatch(setSuperSimplifierAccountParameter(event.target.value));
    dispatch(
      getSuperSimplifierAccountAsync({
        accountNumber: event.target.value,
      })
    );
  };

  let errorMessage = null;
  if (supersimplifierStore.superSimplifierAccountState === ScreenState.ErrorUnableToLoadData) {
    errorMessage =
      'There is currently no insurance information for this account. If the account has been recently established it may take a few days for this data to be populated. Please contact your adviser or our client service team for assistance.';
    premiumDetailsTable = [];
    coverSummaryTable = [];
  } else if (supersimplifierStore.superSimplifierAccountState === ScreenState.Loading) {
    premiumDetailsTable = [];
    coverSummaryTable = [];
  }

  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="24px" mb={6}>
        <Box display="flex" alignItems="center" gap="16px" mt={2}>
          <Breadcrumbs>
            <Typography
              onClick={() => history.push(homeUrl())}
              sx={{
                display: 'flex',
                alignItems: 'center',
                ':hover': {
                  cursor: 'pointer',
                },
              }}
              color="inherit"
            >
              <Home sx={{ mr: 0.5 }} fontSize="small" />
              Dashboard
            </Typography>
            <Typography fontWeight={600} fontSize="20px" color="#222222">
              Insurance
            </Typography>
          </Breadcrumbs>

          <Box flex={1} />
          <FormControl>
            <InputLabel id="selected-account-select-label">Account</InputLabel>
            <Select
              labelId="selected-account-select-label"
              id="selected-account-select"
              value={selectedAccount}
              defaultValue={selectedAccount}
              label="Account"
              onChange={handleAccountChange}
            >
              {supersimplifierStore?.superSimplifierAccounts?.data?.map((i) => (
                <MenuItem key={i.accountNumber} value={i.accountNumber ?? ''}>{`${i.accountNumber ?? ''} - ${i.name ?? ''}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {errorMessage && (
          <Alert variant="outlined" severity="info">
            {errorMessage}
          </Alert>
        )}

        {!errorMessage && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <PageContent skeletons={1} isLoading={supersimplifierStore.superSimplifierAccountState === ScreenState.Loading}>
              <SupersimplifierTable tableHeaders={tableHeaders} data={coverSummaryTable} header="Cover Summary" />
            </PageContent>
            <PageContent skeletons={1} isLoading={supersimplifierStore.superSimplifierAccountState === ScreenState.Loading}>
              <SupersimplifierTable tableHeaders={tableHeaders} data={premiumDetailsTable} header="Premium Details" />
            </PageContent>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Insurance;
